import { API } from "aws-amplify/"
import { navigate } from "gatsby"
import { getGlobal } from "reactn"
import { cartEmpty, emptyCart } from "./rootstate"
import * as Sentry from "@sentry/gatsby"

var cartState = {}
let cartStatePromise
let isFetchingCart = false
// Store latest email from LinkAuthenticationElement for immediate access
let latestEmailFromLink = null

const post = (promise, store, path, body, options, updateAndSave, background = false) => {
  // Don't set calculating for background fetches (prefetch) - they should be silent
  let newState = background
    ? { ...store }
    : { ...store, ...{ calculating: true } }

  updateAndSave(newState)
  try {
    const postData = {
      headers: {
        "Content-Type": "application/json",
      },
      body: body,
    }

    API.post("api", path, postData)
      .then((data) => {
        if (data.ss) {
          const shippingOptions = data.so ? data.so : []

          // Shipping is always first item in response - auto-select it
          const shippingID = data.ts?.shipping_line?.id || (shippingOptions[0]?.id || null)

          newState = {
            ...newState,
            ...{
              cartInitialised: true,
              emailAddress: data?.ts?.email ? data.ts.email : "",
              ss: data.ss,
              ts: data.ts,
              os: data.os,
              pd: data.pd ? data.pd : {},
              so: shippingOptions,
              soe: data.soe ? data.soe : [],
              shippingID: shippingID,
              // If main cart response includes express payment intent, save it immediately
              // This allows express payments to show immediately without waiting for separate fetch
              ...(data.expressPd ? {
                expressCartData: {
                  expressPd: data.expressPd,
                  ts: data.ts, // Use same totals for express cart
                  ss: data.ss
                }
              } : {}),
            },
          }

          // Extract order status flags from backend
          const orderStatus = data.os || {}

          // CV = Cart Valid - if we have a cart response with order ID, cart is valid
          // SV = Shipping Valid (os.sv)
          // Email valid = !invalidEmail && email exists
          // Payment intent = pd.pi exists
          const cartValid = !!(data.ss && data.ss.oid && data.ss.oid !== "")
          const shippingValid = orderStatus.sv === true
          const emailValid = !orderStatus.invalidEmail && data.ts?.email?.trim() !== ""
          const hasPaymentIntent = !!data.pd?.pi

          // Page determination logic:
          // - Always show shipping first (Cart -> Confirm Shipping -> Pay)
          // - Only show payment if user clicked "CONTINUE TO PAYMENT" (preparePayment is true)
          // - This ensures we never skip the shipping confirmation step
          const shouldShowPayment = cartValid && shippingValid && emailValid && hasPaymentIntent
          const userRequestedPayment = store.preparePayment // User clicked "CONTINUE TO PAYMENT"

          console.log("[CART RESPONSE] Page determination:", {
            cartValid,
            shippingValid,
            emailValid,
            hasPaymentIntent,
            shouldShowPayment,
            userRequestedPayment,
            note: userRequestedPayment ? "User clicked CONTINUE TO PAYMENT - showing payment" : "Showing shipping (user must confirm first)"
          })

          // Only show payment if:
          // 1. All conditions are met (shouldShowPayment)
          // 2. AND user explicitly clicked "CONTINUE TO PAYMENT" (preparePayment is true)
          // Otherwise, always show shipping first
          if (shouldShowPayment && userRequestedPayment) {
            newState = {
              ...newState,
              showPayment: true,
              showShipping: false,
              accordianItem: 2,
              calculating: false,
              preparePayment: false, // Reset after showing payment
            }
          } else {
              newState = {
                ...newState,
              showPayment: false,
              showShipping: true,
              accordianItem: 0,
              calculating: false,
              // CRITICAL: If user hasn't requested payment, ensure preparePayment is false
              // This prevents accidentally showing payment if preparePayment was set elsewhere
              preparePayment: userRequestedPayment ? store.preparePayment : false
            }
          }

          console.log("NEW STATE", newState)

          // Check if cart is empty BEFORE updating state
          if (!data.ss.oid || data.ss.oid == "") {
            newState = emptyCart(newState)
            // Remove cartOpen from newState so updateAndSave preserves current value
            // This is critical for background fetches - if user opens cart while prefetch
            // is in progress, we don't want to close it with stale store.cartOpen value
            delete newState.cartOpen
            // Also preserve calculating state for background fetches
            if (background) {
              newState.calculating = false // Background fetches shouldn't show calculating
            }
            // updateAndSave will preserve current cartOpen automatically
            updateAndSave(newState)
            return // Don't continue processing if cart is empty
          }

          // Only update if cart is not empty
          // For background fetches, ensure calculating is false
          if (background) {
            newState.calculating = false
            // CRITICAL: Remove cartOpen from newState for background fetches
            // so updateAndSave preserves current value (user might have opened cart)
            delete newState.cartOpen
          }
          updateAndSave(newState)
        } else {
          // No cart data - ensure calculating is cleared
          newState = {
            ...newState,
            calculating: false,
            showPayment: false,
            showShipping: true,
            accordianItem: 0,
          }
          // CRITICAL: Remove cartOpen from newState so updateAndSave preserves current value
          delete newState.cartOpen
          updateAndSave(newState)
        }
      })
      .finally(() => {
        isFetchingCart = false // Reset flag when fetch completes
        // CRITICAL: Create fresh state object - don't spread newState which might have stale cartOpen
        // Don't include cartOpen - updateAndSave will preserve current value from getGlobal()
        const finalState = {
          calculating: false,
          isFetchingCart: false,
          countryChange: false,
        }
        updateAndSave(finalState)
      })
      .catch((err) => {
        isFetchingCart = false // Reset flag on error
        console.error('[NETWORK ERROR] Cart fetch failed:', err);
        // Capture error in Sentry
        if (typeof window !== 'undefined' && typeof Sentry !== 'undefined') {
          console.log('[SENTRY] Capturing cart fetch error to Sentry');
          Sentry.captureException(err, {
            tags: {
              source: 'fetchCartState',
              endpoint: '/checkout/cart',
              errorType: err?.name || 'Unknown'
            },
            extra: {
              endpoint: '/checkout/cart',
              path: path,
              errorMessage: err?.message,
              errorStack: err?.stack,
              background: background
            },
            level: 'error'
          });
        } else {
          console.warn('[SENTRY] Sentry not available to capture error!');
        }
        // CRITICAL: Create fresh state object - don't spread newState which might have stale cartOpen
        // Don't include cartOpen - updateAndSave will preserve current value
        const errorState = {
          calculating: false,
          isFetchingCart: false,
          countryChange: false,
          error: err,
        }
        updateAndSave(errorState)
      })
  } catch (e) {
    isFetchingCart = false // Reset flag on exception
    console.error('[NETWORK ERROR] Cart fetch exception:', e);
    // Capture error in Sentry
    if (typeof window !== 'undefined' && typeof Sentry !== 'undefined') {
      console.log('[SENTRY] Capturing cart fetch exception to Sentry');
      Sentry.captureException(e, {
        tags: {
          source: 'fetchCartState_exception',
          endpoint: '/checkout/cart',
          errorType: e?.name || 'Unknown'
        },
        extra: {
          endpoint: '/checkout/cart',
          path: path,
          errorMessage: e?.message,
          errorStack: e?.stack,
          background: background
        },
        level: 'error'
      });
    } else {
      console.warn('[SENTRY] Sentry not available to capture exception!');
    }
    // CRITICAL: Create fresh state object - don't spread newState which might have stale cartOpen
    // Don't include cartOpen - updateAndSave will preserve current value
    const exceptionState = {
      calculating: false,
      isFetchingCart: false,
      error: e,
    }
    updateAndSave(exceptionState)
  }
}

export function clearShipping() {
  updateAndSave({
    showPayment: false,
    packagingID: "",
    shippingID: "",
    so: [],
    soe: [],
    sr: {},
  })
}

export async function fetchCartState(store, updateAndSave, force, background = false) {
  console.log("[NETWORK DEBUG] fetchCartState called", {
    cartInitialised: store.cartInitialised,
    checkoutOpen: store.checkoutOpen,
    hasSS: !!store.ss,
    force: force,
    isFetchingCart: isFetchingCart,
    stack: new Error().stack.split("\n").slice(1, 4).join("\n"),
  })

  // Prevent duplicate fetches if one is already in progress
  // BUT allow if preparePayment is true (user clicked "Continue to Payment") or force is true
  // OR if this is a background fetch (prefetch) - those can queue
  // BUT if there's already a user-initiated fetch, skip background fetches
  if (isFetchingCart && !store.preparePayment && !force && !background) {
    console.log(
      "[NETWORK DEBUG] fetchCartState - skipping (fetch already in progress, not background)"
    )
    return
  }

  // If there's a fetch in progress and this is a background fetch, skip it
  // Background fetches should not interfere with user-initiated fetches
  if (isFetchingCart && background && (store.preparePayment || force)) {
    console.log(
      "[NETWORK DEBUG] fetchCartState - skipping background fetch (user-initiated fetch in progress)"
    )
    return
  }

  // Don't fetch cart on payment page - it's a standalone page
  if (
    typeof window !== "undefined" &&
    window.location.pathname.includes("/payment")
  ) {
    console.log("[NETWORK DEBUG] fetchCartState - skipping (payment page)")
    return
  }

  // If cart is already initialised and we're not forcing, skip
  // UNLESS preparePayment is true - we need to fetch to update cart before moving to payment
  // Background fetches should also skip if cart is already initialised
  if (store.cartInitialised && !force && !store.preparePayment) {
    console.log(
      "[NETWORK DEBUG] fetchCartState - skipping (cart already initialised, force=false, preparePayment=false, background=" + background + ")"
    )
    return
  }

  // Use existing ConfirmationToken if available (only created when "PAY NOW" is clicked)
  let confirmationTokenId = store.confirmationTokenId || null

  console.log("[NETWORK DEBUG] ConfirmationToken ID for fetchCart:", confirmationTokenId || "null")

  // Set flag to prevent duplicate fetches
  isFetchingCart = true
  // Update store with fetching state
  updateAndSave({ isFetchingCart: true })

  const items = store.cartProducts
    ? store.cartProducts.map((item) => ({
        ownGarment: item.own_garment,
        kvatt: item.kvatt,
        sku: item.sku,
        quantity: item.quantity,
      }))
    : []

  // Get the latest email - check ref first (immediately updated), then global state, then store
  const latestGlobal = getGlobal()
  const emailToUse = latestEmailFromLink || latestGlobal.emailAddress || store.emailAddress

  console.log('[CART FETCH DEBUG] fetchCartState called with email:', {
    emailFromStore: store.emailAddress,
    emailFromLatestGlobal: latestGlobal.emailAddress,
    emailFromLinkRef: latestEmailFromLink,
    emailToUse: emailToUse,
    tsEmail: store.ts?.email,
    stack: new Error().stack.split('\n').slice(1, 4).join('\n')
  })

  post(
    cartStatePromise,
    store,
    "/checkout/cart",
    {
      ss: store.ss,
      ca: {
        items: items,
        promo:
          store.promoCode && store.promoCode.promo_code
            ? store.promoCode.promo_code
            : "",
        // Shipping Address
        sa: {
          ...store.shipping_address,
          phone: store.contactNumber,
          name: store.fullName,
        },
        // Shipping & Packaging
        sp: {
          shippingID: store.shippingID,
          packagingID: store.packagingID,
        },
        cd: {
          email: emailToUse,
          marketing: store.marketing,
        },
        // Radar Session ID for enhanced fraud detection
        radarID: store.radarID || null,
        // ConfirmationToken ID (if created)
        confirmation_token_id: confirmationTokenId || null,
      },
    },
    null,
    updateAndSave,
    background // Pass background flag to post function
  )
}

/**
 * Warm up the cart lambda without affecting user state
 * This function sends a warmup ping to the same cart endpoint with a warmup flag
 */
export function warmUpCartLambda() {
  if (typeof window === "undefined") return

  try {
    const postData = {
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        warmup: true,
        timestamp: Date.now(),
      }),
    }

    API.post("api", "/checkout/cart", postData)
      .then(() => {})
      .catch((error) => {
        // Capture error in Sentry
        if (typeof window !== 'undefined') {
          Sentry.captureException(error, {
            tags: { source: 'warmUpCartLambda' },
            extra: { endpoint: '/checkout/cart' }
          })
        }
      })
  } catch (error) {
    // Capture error in Sentry
    if (typeof window !== 'undefined') {
      Sentry.captureException(error, {
        tags: { source: 'warmUpCartLambda_exception' },
        extra: { endpoint: '/checkout/cart' }
      })
    }
  }
}

/**
 * Fetch payment receipt data
 * @param {string} paymentIntent - Payment intent ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Payment receipt data
 */
export const fetchReceipt = async (paymentIntent, orderId) => {
  const data = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      payment_intent: paymentIntent,
      order_id: orderId,
    }),
  }

  return API.post("api", "/checkout/receipt", data)
}

/**
 * Fetch payment intent data
 * @param {string} paymentIntent - Payment intent ID
 * @param {string} orderId - Order ID
 * @returns {Promise<Object>} Payment intent data
 */
export const fetchPaymentIntent = async (paymentIntent, orderId) => {
  const data = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: {
      payment_intent: paymentIntent,
      order_id: orderId,
    },
  }

  return API.post("api", "/intent/retrieve", data)
}

/**
 * Confirm payment using ConfirmationToken
 * Backend validates shipping/email, creates customer, and confirms payment
 * @param {string} confirmationTokenId - Stripe ConfirmationToken ID
 * @param {string} paymentIntentId - Stripe PaymentIntent ID
 * @param {string} orderId - Shopify order ID
 * @returns {Promise<Object>} Payment confirmation result
 * May return { success: true, ... } or { requires_action: true, next_action: { type: 'redirect_to_url', redirect_to_url: { url: '...' } } }
 */
export const confirmPayment = async (confirmationTokenId, paymentIntentId, orderId) => {
  const data = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      confirmation_token_id: confirmationTokenId,
      payment_intent_id: paymentIntentId,
      order_id: orderId,
    }),
  }

  try {
    const result = await API.post("api", "/checkout/confirm-payment   ", data)

    // Handle 3D Secure redirect case
    if (result?.status === 'requires_action' && result?.next_action?.type === 'redirect_to_url') {
      console.log('[CONFIRM PAYMENT] 3D Secure redirect required:', result.next_action.redirect_to_url.url)
      return {
        requires_action: true,
        redirect_url: result.next_action.redirect_to_url.url,
        payment_intent_id: result.payment_intent_id || paymentIntentId,
        order_id: result.order_id || orderId
      }
    }

    return result
  } catch (error) {
    // Capture error in Sentry
    if (typeof window !== 'undefined') {
      Sentry.captureException(error, {
        tags: { source: 'confirmPayment' },
        extra: {
          endpoint: '/checkout/confirm-payment',
          confirmationTokenId,
          paymentIntentId,
          orderId
        }
      })
    }
    // AWS Amplify errors can have different structures
    // Return error in consistent format for frontend handling
    console.error("[NETWORK] confirmPayment error:", error)

    // Extract error message from various possible formats
    let errorMessage = "Payment processing failed. Please try again."

    if (error?.response?.data?.error) {
      errorMessage = error.response.data.error
    } else if (error?.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error?.message) {
      // Don't show raw error messages like "Request failed with status code 404"
      if (error.message.includes("status code")) {
        if (error.message.includes("404")) {
          errorMessage = "Payment service unavailable. Please try again in a moment."
        } else if (error.message.includes("400")) {
          errorMessage = "Invalid payment details. Please check and try again."
        } else if (error.message.includes("500")) {
          errorMessage = "Server error. Please try again in a moment."
        } else {
          errorMessage = "Payment service error. Please try again."
        }
      } else {
        errorMessage = error.message
      }
    }

    return {
      error: errorMessage,
      originalError: error
    }
  }
}

/**
 * Fetch express checkout shipping options
 * @param {Object} store - Current store state
 * @returns {Promise<Object>} Express checkout shipping options
 */
export const fetchExpressShippingOptions = async (store) => {
  try {
    const items = store.cartProducts
      ? store.cartProducts.map((item) => ({
          ownGarment: item.own_garment,
          kvatt: item.kvatt,
          sku: item.sku,
          quantity: item.quantity,
        }))
      : []

    const cartData = {
      ss: store.ss,
      ca: {
        items: items,
        promo:
          store.promoCode && store.promoCode.promo_code
            ? store.promoCode.promo_code
            : "",
        // Use current shipping address from global state
        sa: store.shipping_address || {
          country_code: "GB",
          zip: "EC2Y 5HN",
          address1: "",
          address2: "",
          city: "London",
          locality: "",
          name: "",
          phone: "",
          complete: true,
        },
        // Use current shipping & packaging
        sp: {
          shippingID: store.shippingID,
          packagingID: store.packagingID,
        },
        cd: {
          email: store.emailAddress,
        },
      },
    }

    const postData = {
      headers: {
        "Content-Type": "application/json",
      },
      body: cartData,
    }

    const result = await API.post("api", "/checkout/cart", postData)
    return result
  } catch (error) {
    throw error
  }
}

/**
 * Set the latest email from LinkAuthenticationElement for immediate use in cart fetches
 * This is needed because updateGlobal is async and cart fetches might happen before the update propagates
 */
export const setLatestEmailFromLink = (email) => {
  latestEmailFromLink = email
  console.log('[EMAIL REF DEBUG] Setting latestEmailFromLink to:', email)
}
